import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import pdfjs from '@/utils/pdfjs';
import { SIGNATURE_BOX_RATIO } from '@/constants/DocumentTypes';
/** Upper bound for how many CSS pixels a PDF point gets; narrower containers render smaller. */
const MAX_SCALE = 1.5;
const MIN_WIDTH = 10;
let PdfFieldCanvas = class PdfFieldCanvas extends Vue {
    src;
    page;
    fields;
    values;
    editable;
    selected;
    document = null;
    scale = MAX_SCALE;
    pointer = null;
    mounted() {
        this.load();
    }
    beforeDestroy() {
        this.stopPointer();
        this.document?.destroy();
    }
    onSrcChange() {
        this.load();
    }
    onPageChange() {
        this.renderPage();
    }
    async load() {
        this.document?.destroy();
        this.document = null;
        if (!this.src)
            return;
        const source = typeof this.src === 'string' ? { url: this.src } : { data: this.src };
        this.document = await pdfjs.getDocument(source).promise;
        this.$emit('loaded', this.document.numPages);
        await this.renderPage();
    }
    /** Not render(): Vue reserves that name for the component's own render function. */
    async renderPage() {
        if (!this.document)
            return;
        const page = await this.document.getPage(this.page);
        const viewport = page.getViewport({ scale: this.fitScale(page) });
        const canvas = this.$refs.canvas;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        this.$emit('rendered');
    }
    /** The whole page has to fit the column, otherwise the field boxes drift off the rendered form. */
    fitScale(page) {
        const width = page.getViewport({ scale: 1 }).width;
        this.scale = Math.min(MAX_SCALE, this.availableWidth() / width) || MAX_SCALE;
        return this.scale;
    }
    /** The parent's content box, minus our own border; the canvas may not be wider or it gets squashed. */
    availableWidth() {
        const parent = this.$el.parentElement;
        if (!parent)
            return 0;
        const style = getComputedStyle(parent);
        const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        return Math.max(0, parent.clientWidth - padding - 2);
    }
    label(field) {
        if (field.kind === 'signature')
            return '[podpis]';
        if (field.kind === 'text')
            return field.value ?? '';
        return this.editable ? `{{${field.key}}}` : (this.values[field.key ?? ''] ?? '');
    }
    /** Fields carry the top edge of their box; a signature box is a third as tall as it is wide. */
    boxStyle(field) {
        const height = field.kind === 'signature' ? field.width / SIGNATURE_BOX_RATIO : field.fontSize;
        return {
            left: `${field.x * this.scale}px`,
            top: `${field.y * this.scale}px`,
            width: `${field.width * this.scale}px`,
            height: `${height * this.scale}px`,
            fontSize: `${field.fontSize * this.scale}px`,
            lineHeight: `${height * this.scale}px`,
        };
    }
    startMove(event, index) {
        if (!this.editable)
            return;
        this.$emit('select', index);
        this.startPointer(event, index, 'move');
    }
    startResize(event, index) {
        this.$emit('select', index);
        this.startPointer(event, index, 'resize');
    }
    startPointer(event, index, mode) {
        event.preventDefault();
        this.pointer = { index, mode, clientX: event.clientX, clientY: event.clientY, origin: { ...this.fields[index] } };
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.stopPointer);
    }
    onPointerMove(event) {
        if (!this.pointer)
            return;
        const { origin, mode, index } = this.pointer;
        const dx = (event.clientX - this.pointer.clientX) / this.scale;
        const dy = (event.clientY - this.pointer.clientY) / this.scale;
        const moved = mode === 'move'
            ? { ...origin, x: Math.max(0, Math.round(origin.x + dx)), y: Math.max(0, Math.round(origin.y + dy)) }
            : { ...origin, width: Math.max(MIN_WIDTH, Math.round(origin.width + dx)) };
        const fields = this.fields.slice();
        fields[index] = moved;
        this.$emit('input', fields);
    }
    stopPointer() {
        this.pointer = null;
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.stopPointer);
    }
};
__decorate([
    Prop({ required: true })
], PdfFieldCanvas.prototype, "src", void 0);
__decorate([
    Prop({ default: 1 })
], PdfFieldCanvas.prototype, "page", void 0);
__decorate([
    Prop({ default: () => [] })
], PdfFieldCanvas.prototype, "fields", void 0);
__decorate([
    Prop({ default: () => ({}) })
], PdfFieldCanvas.prototype, "values", void 0);
__decorate([
    Prop({ default: false })
], PdfFieldCanvas.prototype, "editable", void 0);
__decorate([
    Prop({ default: null })
], PdfFieldCanvas.prototype, "selected", void 0);
__decorate([
    Watch('src')
], PdfFieldCanvas.prototype, "onSrcChange", null);
__decorate([
    Watch('page')
], PdfFieldCanvas.prototype, "onPageChange", null);
PdfFieldCanvas = __decorate([
    Component
], PdfFieldCanvas);
export default PdfFieldCanvas;
